import {
    Component,
    ChangeDetectionStrategy,
    HostListener,
    inject,
    Injector,
    OnInit,
    OnDestroy,
    ElementRef,
    ViewChild,
    AfterViewInit,
    ViewContainerRef,
    createComponent,
    ApplicationRef,
    EnvironmentInjector,
} from '@angular/core';
import { ModalService, ModalOptions } from './modal.service';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { DOCUMENT } from '@angular/common';

@Component({
    selector: 'app-modal',
    standalone: true,
    templateUrl: './modal.component.html',
    styleUrls: ['./modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, NgClass, NgTemplateOutlet],
})
export class ModalComponent implements OnInit, OnDestroy {
    modalService = inject(ModalService);
    modals = this.modalService.modal$;
    injector = inject(Injector);
    private document = inject(DOCUMENT);
    private appRef = inject(ApplicationRef);
    private envInjector = inject(EnvironmentInjector);

    @ViewChild('modalContainer', { static: false }) modalContainer?: ElementRef<HTMLDivElement>;

    private previouslyFocusedElement: HTMLElement | null = null;
    private originalBodyOverflow = '';
    private modalCount = 0;
    private componentRefs = new Map<number, any>();

    ngOnInit() {
        // Subscribe to modal changes to manage focus and scroll
        this.modals.subscribe((modals) => {
            const visibleModals = modals.filter((modal) => modal.show);

            if (visibleModals.length > this.modalCount) {
                // Modal opened
                this.onModalOpened();

                // Load dynamic component if needed
                const newModal = visibleModals[visibleModals.length - 1];
                if (newModal.contentComponent) {
                    // Wait for DOM to render with slightly longer delay
                    setTimeout(() => this.loadDynamicComponent(newModal), 100);
                }
            } else if (visibleModals.length < this.modalCount) {
                // Modal closed
                this.onModalClosed(visibleModals.length === 0);

                // Cleanup component refs
                modals.forEach((modal) => {
                    if (!modal.show && modal.id && this.componentRefs.has(modal.id)) {
                        const componentRef = this.componentRefs.get(modal.id);
                        if (componentRef) {
                            this.appRef.detachView(componentRef.hostView);
                            componentRef.destroy();
                            this.componentRefs.delete(modal.id);
                        }
                    }
                });
            }

            this.modalCount = visibleModals.length;
        });
    }

    private loadDynamicComponent(modal: ModalOptions) {
        if (!modal.contentComponent || !modal.id) return;

        // Check if component already loaded for this modal
        if (this.componentRefs.has(modal.id)) {
            console.log('Component already loaded for modal:', modal.id);
            return;
        }

        // Find the container for this specific modal
        const container = this.document.querySelector(`[data-modal-id="${modal.id}"] .dynamic-component-container`);
        if (!container) {
            console.error('Dynamic component container not found for modal:', modal.id);
            console.error('Available modals in DOM:', this.document.querySelectorAll('[data-modal-id]'));
            // Retry after a short delay
            setTimeout(() => {
                const retryContainer = this.document.querySelector(
                    `[data-modal-id="${modal.id}"] .dynamic-component-container`
                );
                if (retryContainer) {
                    this.loadComponentIntoContainer(modal, retryContainer);
                } else {
                    console.error('Retry failed - container still not found for modal:', modal.id);
                }
            }, 50);
            return;
        }

        this.loadComponentIntoContainer(modal, container);
    }

    private loadComponentIntoContainer(modal: ModalOptions, container: Element) {
        // Clear any existing content in the container
        container.innerHTML = '';

        // Create the component
        const componentRef = createComponent(modal.contentComponent!, {
            environmentInjector: this.envInjector,
            elementInjector: this.injector,
        });

        // Assign any data to the component instance
        if (modal.contentData) {
            Object.assign(componentRef.instance, modal.contentData);
        }

        // Attach to ApplicationRef to ensure change detection works
        this.appRef.attachView(componentRef.hostView);

        // Insert into DOM
        container.appendChild(componentRef.location.nativeElement);

        // Store reference for cleanup
        this.componentRefs.set(modal.id!, componentRef);

        console.log('Successfully loaded component for modal:', modal.id);
    }

    ngOnDestroy() {
        // Restore focus and scroll when component is destroyed
        this.restoreFocus();
        this.restoreBodyScroll();
    }

    private onModalOpened() {
        // Store currently focused element
        if (!this.previouslyFocusedElement) {
            this.previouslyFocusedElement = this.document.activeElement as HTMLElement;
        }

        // Disable body scroll
        this.disableBodyScroll();

        // Focus will be set after view is rendered
        setTimeout(() => this.trapFocus(), 0);
    }

    private onModalClosed(allClosed: boolean) {
        if (allClosed) {
            this.restoreFocus();
            this.restoreBodyScroll();
        }
    }

    private disableBodyScroll() {
        const body = this.document.body;
        if (!this.originalBodyOverflow) {
            this.originalBodyOverflow = body.style.overflow || '';
            // Get scrollbar width to prevent content jump
            const scrollbarWidth = window.innerWidth - this.document.documentElement.clientWidth;
            body.style.paddingRight = `${scrollbarWidth}px`;
        }
        body.style.overflow = 'hidden';
    }

    private restoreBodyScroll() {
        const body = this.document.body;
        body.style.overflow = this.originalBodyOverflow;
        body.style.paddingRight = '';
        this.originalBodyOverflow = '';
    }

    private trapFocus() {
        // Find the first focusable element in the modal
        const focusableElements = this.getFocusableElements();
        if (focusableElements.length > 0) {
            focusableElements[0].focus();
        }
    }

    private restoreFocus() {
        if (this.previouslyFocusedElement) {
            this.previouslyFocusedElement.focus();
            this.previouslyFocusedElement = null;
        }
    }

    private getFocusableElements(): HTMLElement[] {
        if (!this.modalContainer) return [];

        const focusableSelectors = [
            'button:not([disabled])',
            '[href]',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])',
        ].join(', ');

        return Array.from(this.modalContainer.nativeElement.querySelectorAll(focusableSelectors));
    }

    @HostListener('document:keydown.escape')
    handleEscape() {
        const modals = this.modalService.currentModals;
        if (modals.length) {
            const topModal = modals[modals.length - 1];
            if (topModal.keyboardClose !== false) {
                this.modalService.close(topModal.id);
            }
        }
    }

    @HostListener('keydown.tab', ['$event'])
    handleTab(event: KeyboardEvent) {
        const focusableElements = this.getFocusableElements();
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
            // Shift + Tab
            if (this.document.activeElement === firstElement) {
                event.preventDefault();
                lastElement.focus();
            }
        } else {
            // Tab
            if (this.document.activeElement === lastElement) {
                event.preventDefault();
                firstElement.focus();
            }
        }
    }

    @HostListener('document:keydown.enter', ['$event'])
    handleEnter(event: KeyboardEvent) {
        // Prevent duplicate modal opening from button that triggered it
        const target = event.target as HTMLElement;
        if (target && this.modalService.currentModals.length > 0) {
            // If there's already a modal open and the focused element is outside modals
            const isInsideModal = target.closest('.app-modal-box');
            if (!isInsideModal) {
                event.preventDefault();
                event.stopPropagation();
            }
        }
    }

    backdropClick(event: MouseEvent, id: number) {
        const modal = this.modalService.currentModals.find((m: ModalOptions) => m.id === id);
        if (
            modal?.backdropDismiss !== false &&
            (event.target as HTMLElement).classList.contains('app-modal-backdrop')
        ) {
            this.modalService.close(id);
        }
    }

    contentClick(event: MouseEvent, id: number) {
        const target = event.target as HTMLElement;
        if (
            target.hasAttribute('data-cancel') ||
            target.hasAttribute('data-confirm') ||
            target.hasAttribute('data-close')
        ) {
            this.modalService.close(id);
            event.stopPropagation();
        }
    }
}
